import { useEffect, useLayoutEffect, useState } from "react";

const S = (sel, title, body, view) => ({ sel, title, body, view });
const START = S("[data-testid=nav-overview]", "Welcome to your workspace", "This quick tour shows where everything lives. Use Next or the arrow keys; press Esc to skip.", "overview");
const END = [
  S("[data-testid=nav-plans]", "Plans & Billing", "Upgrade for higher limits and lower fees. Pay annually to save 20%."),
  S("[data-testid=notification-button]", "Notifications", "Every bid, award, payment and dispatch update appears here. Click an alert to jump to it."),
  S("[data-testid=open-guide-button]", "Your user guide", "Open the full step-by-step guide any time. It's view-only and personalised to your account."),
  S("[data-testid=nav-support]", "Need help?", "Raise a ticket here or use the Need help? button at the bottom-right of every page."),
];
const STEPS = {
  buyer: [START,
    S("[data-testid=nav-jobs]", "Post & manage RFQs", "Click + New request to describe your job. Verified printers send sealed bids; compare them and award the best one here."),
    S("[data-testid=nav-briefs]", "Design briefs", "Need artwork? Post a design brief and hire a verified agency, with payment held in escrow."),
    S("[data-testid=nav-directory]", "Supplier network", "Browse verified agencies and partners, and hire an agency directly."),
    S("[data-testid=nav-financials]", "Financials", "Download GST invoices for every order."),
    S("[data-testid=nav-sample-packs]", "Sample packs", "Request physical substrate and finish samples before a big run."),
    ...END],
  printer: [START,
    S("[data-testid=nav-capabilities]", "Set your capabilities", "Tell us your presses, substrates and finishes. Jobs matching your profile are sent to you."),
    S("[data-testid=nav-jobs]", "Job Board", "Open a sealed auction and click Submit sealed bid. Your identity stays hidden from the buyer."),
    S("[data-testid=nav-milestones]", "My Orders", "Won jobs appear here. Post milestones and dispatch with blind labels."),
    S("[data-testid=nav-sourcing]", "Materials & Finishing", "Raise sub-RFQs for paper, board, ink or outsourced finishing, for platform jobs or your own jobs."),
    S("[data-testid=nav-financials]", "Financials", "Settlements, invoices and bank details for payouts."),
    S("[data-testid=nav-bid-calculator]", "Bid Calculator", "See exactly what lands in your bank after fees, GST and TDS."),
    ...END],
  agency: [START,
    S("[data-testid=nav-studio]", "Studio profile", "Add specialities and portfolio pieces. Buyers find and hire you from here."),
    S("[data-testid=nav-design-services]", "Design inbox", "Briefs matching your specialities. Send a quote with price, timeline and revisions."),
    S("[data-testid=nav-briefs]", "Design projects", "Upload drafts, handle revisions and deliver final files."),
    S("[data-testid=nav-financials]", "Financials", "Settlements, invoices and bank details for payouts."),
    ...END],
  material_supplier: [START,
    S("[data-testid=nav-capabilities]", "Set your capabilities", "Choose the materials you supply, your MOQ and service area, and turn on RFQ alerts."),
    S("[data-testid=nav-job-board]", "Job Board", "Buyer auctions and printer sub-RFQs. Submit sealed quotes with price and lead time."),
    S("[data-testid=nav-partner-orders]", "My Orders", "Won orders: dispatch, add tracking, and get paid when receipt is confirmed."),
    S("[data-testid=nav-financials]", "Financials", "Invoices and bank details for payouts."),
    ...END],
  finishing_partner: [START,
    S("[data-testid=nav-capabilities]", "Set your capabilities", "Choose your processes (lamination, foiling, die-cutting…), capacity and turnaround."),
    S("[data-testid=nav-job-board]", "Job Board", "Outsourced finishing RFQs from printers. Submit sealed quotes."),
    S("[data-testid=nav-partner-orders]", "My Orders", "Won jobs: deliver, add tracking, and get paid when receipt is confirmed."),
    S("[data-testid=nav-financials]", "Financials", "Invoices and bank details for payouts."),
    ...END],
};

export function GuideTour({ role, setView, onDone }) {
  const steps = (STEPS[role] || []).filter(s => document.querySelector(s.sel));
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[i];
  useEffect(() => { if (step?.view && setView) setView(step.view); }, [step, setView]);
  useLayoutEffect(() => {
    if (!step) return;
    const el = document.querySelector(step.sel);
    if (!el) return;
    el.scrollIntoView({ block: "nearest" });
    const upd = () => setRect(el.getBoundingClientRect());
    upd();
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, [step]);
  useEffect(() => {
    const k = (e) => {
      if (e.key === "Escape") onDone();
      if (e.key === "ArrowRight") setI(n => (n + 1 < steps.length ? n + 1 : n));
      if (e.key === "ArrowLeft") setI(n => Math.max(0, n - 1));
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [steps.length, onDone]);
  if (!step || !rect) return null;
  const pad = 6;
  const right = rect.left < window.innerWidth / 2;
  const cardStyle = right
    ? { top: Math.min(rect.top, window.innerHeight - 220), left: rect.right + 18 }
    : { top: rect.bottom + 14, right: Math.max(16, window.innerWidth - rect.right) };
  return (
    <div className="tour-root" data-testid="guide-tour">
      <div className="tour-spot" style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }}/>
      <div className="tour-card" style={cardStyle} data-testid="tour-card">
        <small>Step {i + 1} of {steps.length}</small>
        <b data-testid="tour-title">{step.title}</b>
        <p>{step.body}</p>
        <div className="tour-actions">
          <button className="m-ghost" data-testid="tour-skip" onClick={onDone}>Skip</button>
          <span style={{ flex: 1 }}/>
          {i > 0 && <button className="secondary-btn" data-testid="tour-back" onClick={() => setI(i - 1)}>Back</button>}
          {i + 1 < steps.length
            ? <button className="primary-btn" data-testid="tour-next" onClick={() => setI(i + 1)}>Next</button>
            : <button className="primary-btn" data-testid="tour-finish" onClick={onDone}>Finish</button>}
        </div>
      </div>
    </div>
  );
}
